// Diagnostics for explaining why a given user cannot be assigned to an event
// Centralizes checks used by the Planning UI so they stay aligned with
// optimization constraints and settings.

function dayKey(d) {
  const x = new Date(d);
  const pad = (n) => String(n).padStart(2, '0');
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
}

function mondayOf(d) {
  const x = new Date(d);
  const dow = x.getDay();
  const diff = (dow + 6) % 7;
  const m = new Date(x);
  m.setDate(x.getDate() - diff);
  m.setHours(0, 0, 0, 0);
  return dayKey(m);
}

function effectiveMinutesForEvent(e0) {
  const s = new Date(e0.start).getTime();
  const e = new Date(e0.end || e0.start).getTime();
  const spanMin = Math.max(0, Math.round((e - s) / 60000));
  const eff = Math.max(0, Math.round(Number(e0?.extendedProps?.effectiveMinutes) || spanMin));
  return eff;
}

// Portion of event hours that lands on date `forDate` (YYYY-MM-DD)
function contributeHours(e0, forDate) {
  const s = new Date(e0.start).getTime();
  const ee = new Date(e0.end || e0.start).getTime();
  const span = Math.max(0, ee - s);
  if (!span) return 0;
  const effMin = effectiveMinutesForEvent(e0);
  const effH = effMin / 60;
  const ds = new Date(`${forDate}T00:00:00`).getTime();
  const de = ds + 24 * 3600000;
  const overlap = Math.max(0, Math.min(ee, de) - Math.max(s, ds));
  if (!overlap) return 0;
  return effH * (overlap / span);
}

function getAssignedEventsForUser(allEvents, uid) {
  return (Array.isArray(allEvents) ? allEvents : []).filter((e0) => {
    const arr = Array.isArray(e0?.extendedProps?.assignments)
      ? e0.extendedProps.assignments
      : [];
    return arr.some((a) => a && a.uid === uid);
  });
}

function rolesIntersect(reqRoles, memberRoles) {
  if (!reqRoles.length) return true;
  return memberRoles.some((r) => reqRoles.includes(String(r || '').trim()));
}

// Core function: returns a single blocking reason or 'Available'
export function diagnoseMemberForEvent(ev, member, ctx = {}) {
  if (!ev || !member) return { uid: member?.uid, label: member?.displayName || member?.email || member?.uid, reason: 'Unavailable' };

  const { events = [], optimizationOverrides = {} } = ctx;
  const uid = member.uid;

  const start = new Date(ev.start);
  const end = new Date(ev.end || ev.start);
  const durationMin = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
  const effMin = Math.max(0, Math.round(Number(ev.effectiveMinutes) || durationMin));

  const reqRoles = Array.isArray(ev.rolesRequired)
    ? ev.rolesRequired.map((r) => String(r.role || '').trim()).filter(Boolean)
    : [];
  const memberRoles = Array.isArray(member.roles) ? member.roles.map((r) => String(r || '').trim()) : [];
  if (!rolesIntersect(reqRoles, memberRoles)) {
    return { uid, label: member.displayName || member.email || uid, reason: 'Does not match required roles' };
  }

  const assigned = getAssignedEventsForUser(events, uid);
  // 1) Overlap check
  const overlaps = assigned.some((e1) => {
    const s1 = new Date(e1.start).getTime();
    const e1e = new Date(e1.end || e1.start).getTime();
    return !(e1e <= start.getTime() || s1 >= end.getTime());
  });
  if (overlaps) {
    return { uid, label: member.displayName || member.email || uid, reason: 'Overlap with another assigned event' };
  }

  // 2) Location min break (between different locations)
  const minBreakMin = Number(optimizationOverrides?.locationMinBreakMinutes) || 30;
  const locA = (ev.location || '').trim();
  const locationBreakBreached = assigned.some((e1) => {
    const locB = (e1.location || '').trim();
    if (!locA || !locB || locA === locB) return false;
    const s1 = new Date(e1.start).getTime();
    const e1e = new Date(e1.end || e1.start).getTime();
    const s = start.getTime();
    const e = end.getTime();
    if (e1e <= s) {
      return (s - e1e) < minBreakMin * 60000; // previous ends too close
    }
    if (e <= s1) {
      return (s1 - e) < minBreakMin * 60000; // next starts too close
    }
    return false;
  });
  if (locationBreakBreached) {
    return { uid, label: member.displayName || member.email || uid, reason: 'Min break between locations not met' };
  }

  // 3) Daily cap (hours) apportioned across days
  const dailyCap = Number(optimizationOverrides?.mediaDailyMaxHours) || 10;
  const dayA = dayKey(start);
  const dayB = dayKey(end);
  const userDayHours = (dky) => assigned.reduce((acc, e0) => acc + contributeHours(e0, dky), 0);
  const aBefore = userDayHours(dayA);
  const bBefore = dayB !== dayA ? userDayHours(dayB) : aBefore;
  const addA = contributeHours({ start, end, extendedProps: { effectiveMinutes: effMin } }, dayA);
  const addB = dayB !== dayA ? contributeHours({ start, end, extendedProps: { effectiveMinutes: effMin } }, dayB) : 0;
  if (aBefore + addA > dailyCap + 1e-6 || (dayB !== dayA && bBefore + addB > dailyCap + 1e-6)) {
    return { uid, label: member.displayName || member.email || uid, reason: 'Daily cap exceeded' };
  }

  // 4) Weekly cap (hours)
  const weeklyMax = Number(member.weeklyHoursMax)
    || (member.contract === 'external' ? (Number(optimizationOverrides?.weeklyDefaultExternal) || 44) : (Number(optimizationOverrides?.weeklyDefaultInternal) || 40));
  const weekKey = mondayOf(start);
  const inWeek = (e0) => mondayOf(e0.start) === weekKey;
  const weekHours = assigned
    .filter(inWeek)
    .reduce((acc, e0) => acc + effectiveMinutesForEvent(e0) / 60, 0);
  const addWeek = effMin / 60;
  if (weekHours + addWeek > weeklyMax + 1e-6) {
    return { uid, label: member.displayName || member.email || uid, reason: 'Weekly cap exceeded' };
  }

  // 5) Daily rest (min hours from any previous day end to this start)
  const restMinH = Number(optimizationOverrides?.mediaMinDailyRestHours) || 11;
  const restMs = restMinH * 3600000;
  const prevEndMinGap = assigned.reduce((minGap, e1) => {
    const se = new Date(e1.end || e1.start).getTime();
    const st = start.getTime();
    if (se <= st) {
      const g = st - se;
      return Math.min(minGap, g);
    }
    return minGap;
  }, Infinity);
  if (prevEndMinGap < restMs) {
    return { uid, label: member.displayName || member.email || uid, reason: 'Daily rest not met' };
  }

  // 6) Daily working amplitude cap (first start to last end across day)
  const amplitudeMaxH = Number(optimizationOverrides?.mediaAmplitudeMaxHours) || 11;
  const amplitudeMaxMs = amplitudeMaxH * 3600000;
  const ymd = dayKey(start);
  const sameDayAssigned = assigned.filter((e0) => dayKey(e0.start) === ymd);
  const allSameDay = [
    ...sameDayAssigned,
    { start, end, extendedProps: { effectiveMinutes: effMin } },
  ];
  if (allSameDay.length) {
    const earliest = Math.min(...allSameDay.map((e0) => new Date(e0.start).getTime()));
    const latest = Math.max(...allSameDay.map((e0) => new Date(e0.end || e0.start).getTime()));
    const span = latest - earliest;
    if (span > amplitudeMaxMs + 1e-6) {
      return { uid, label: member.displayName || member.email || uid, reason: 'Daily amplitude cap exceeded' };
    }
  }

  // 7) Max continuous hours within a day (mediaContinuousWork)
  const maxContinuousHours = Number(optimizationOverrides?.mediaMaxContinuousHours) || 6;
  const minBreakMinutes = Number(optimizationOverrides?.mediaMinBreakMinutes) || 20;
  const minBreakMs = minBreakMinutes * 60 * 1000;
  const blockItems = allSameDay
    .map((e0) => ({ s: new Date(e0.start).getTime(), e: new Date(e0.end || e0.start).getTime(), effMin: effectiveMinutesForEvent(e0) }))
    .sort((a, b) => a.s - b.s);
  let block = [];
  const flushBlockCheck = () => {
    if (!block.length) return false;
    const sumContMin = block.reduce((acc, it) => {
      const spanMin = Math.max(0, Math.round((it.e - it.s) / 60000));
      const internalBreakMin = Math.max(0, spanMin - it.effMin);
      const contMin = internalBreakMin >= minBreakMinutes ? Math.max(0, Math.round(it.effMin / 2)) : it.effMin;
      return acc + contMin;
    }, 0);
    return sumContMin > maxContinuousHours * 60 + 1e-6;
  };
  for (let i = 0; i < blockItems.length; i++) {
    const cur = blockItems[i];
    if (!block.length) {
      block.push(cur);
    } else {
      const prev = block[block.length - 1];
      const gap = cur.s - prev.e;
      if (gap < minBreakMs) {
        block.push(cur);
      } else {
        if (flushBlockCheck()) {
          return { uid, label: member.displayName || member.email || uid, reason: 'Max continuous hours exceeded' };
        }
        block = [cur];
      }
    }
  }
  if (flushBlockCheck()) {
    return { uid, label: member.displayName || member.email || uid, reason: 'Max continuous hours exceeded' };
  }

  // 8) Max consecutive work days (mediaConsecutiveDays)
  const maxConsecutive = Number(optimizationOverrides?.mediaMaxConsecutiveDays) || 6;
  if (maxConsecutive > 0) {
    const daySet = new Set(assigned.map((e0) => dayKey(e0.start)));
    daySet.add(ymd);
    const days = Array.from(daySet).map((d) => new Date(d + 'T00:00:00').getTime()).sort((a, b) => a - b);
    let best = 0;
    let run = 0;
    let prev = null;
    const oneDayMs = 24 * 3600000;
    for (const t of days) {
      if (prev != null && t - prev === oneDayMs) {
        run += 1;
      } else {
        run = 1;
      }
      best = Math.max(best, run);
      prev = t;
    }
    if (best > maxConsecutive) {
      return { uid, label: member.displayName || member.email || uid, reason: 'Max consecutive days exceeded' };
    }
  }

  // 9) Min weekly days off (mediaWeeklyDaysOff)
  const minDaysOff = Number(optimizationOverrides?.mediaMinWeeklyDaysOff);
  if (Number.isFinite(minDaysOff) && minDaysOff >= 0) {
    const weekStart = mondayOf(start);
    // Gather working days in this week
    const inSameWeek = (d) => mondayOf(new Date(d)) === weekStart;
    const weekDaysSet = new Set(assigned.filter((e0) => inSameWeek(e0.start)).map((e0) => dayKey(e0.start)));
    weekDaysSet.add(ymd);
    const workDays = weekDaysSet.size;
    const maxWorkDays = Math.max(0, 7 - minDaysOff);
    if (workDays > maxWorkDays) {
      return { uid, label: member.displayName || member.email || uid, reason: 'Weekly days off constraint' };
    }
  }

  // Default
  return { uid, label: member.displayName || member.email || uid, reason: 'Available' };
}

// Build diagnostics rows for all relevant members for the given event
export function diagnoseWhyNotAssignedForEvent(ev, ctx = {}) {
  const { members = [], events = [], optimizationOverrides = {} } = ctx;
  if (!ev) return [];
  const reqRoles = Array.isArray(ev.rolesRequired)
    ? ev.rolesRequired.map((r) => String(r.role || '').trim()).filter(Boolean)
    : [];
  const membersArr = Array.isArray(members) ? members : [];
  const eligible = membersArr.filter((m) => rolesIntersect(reqRoles, Array.isArray(m.roles) ? m.roles : []));
  return eligible.map((m) => diagnoseMemberForEvent(ev, m, { members: membersArr, events, optimizationOverrides }));
}

const assignmentDiagnostics = {
  diagnoseMemberForEvent,
  diagnoseWhyNotAssignedForEvent,
};

export default assignmentDiagnostics;
